import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import styles from "../../_components/Styles/BaseStyleForAdminPanel.module.css";
import UpdateUserModal from "./UpdateUserModal";
import pageStyles from "./UpdateUser.module.css";
import PositionUsersAccordion from "./PositionUsersAccordion";
import { useFetchAllFullInfoUser } from "../../../../hooks/authorization/useFetchAllFullInfoUser";
import { useFetchAllPositions } from "../../../../hooks/authorization/useFetchAllPositions";
import { useFetchAllRights } from "../../../../hooks/authorization/useFetchAllRights";
import { UserUpdate } from "../../../../types/api/authorization/user.types";
import useAppSnackbar from "../../../../hooks/useAppSnackbar";
import { userService } from "../../../../services/authorization/user-service";

interface User {
    id: string;
    firstName: string;
    lastName: string;
    middleName: string;
    login: string;
    email: string;
    password: string;
    position: string[];
    rights: string[];
    status: string;
}

export const UpdateUser = () => {
    const { showSuccess, showError, SnackbarComponent } = useAppSnackbar();
    const { data: usersData, refetch: usersRefetch } = useFetchAllFullInfoUser();
    const { data: positionsData } = useFetchAllPositions();
    const { data: rightsData } = useFetchAllRights();

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedUsers, setSelectedUsers] = useState<Record<string, string>>({});
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    const positions = useMemo(
        () =>
            Array.from(
                new Set(
                    (usersData ?? []).flatMap((user) =>
                        user.positions.map((position) => position.name)
                    )
                )
            ),
        [usersData]
    );

    const usersByPosition = useMemo(() => {
        return positions.reduce((acc, positionTitle) => {
            acc[positionTitle] = (usersData ?? [])
                .filter((user) =>
                    user.positions.some((position) => position.name === positionTitle)
                )
                .map((user) => ({
                    id: user.id,
                    firstName: user.name,
                    lastName: user.surname,
                    middleName: user.lastName,
                    login: user.login,
                    email: user.email,
                    password: "",
                    position: user.positions.map((position) => position.id),
                    rights: user.rights.map((right) => right.id),
                    status: user.statusId,
                }));
            return acc;
        }, {} as Record<string, User[]>);
    }, [positions, usersData]);

    const handleUserSelect = (position: string) => (event: any) => {
        const userId = event.target.value;

        setSelectedUsers((prev) => ({
            ...prev,
            [position]: userId,
        }));

        if (!userId) {
            setSelectedUser(null);
            return;
        }

        const user = usersByPosition[position]?.find((u) => u.id === userId);
        if (user) {
            setSelectedUser(user);
        }
    };

    const handleCloseModal = () => {
        setSelectedUser(null);
        setSelectedUsers({});
    };

    const handleSaveUser = async (userData: any) => {
        if (!selectedUser) return;

        const payload: UserUpdate = {
            id: selectedUser.id,
            name: userData.firstName,
            surname: userData.lastName,
            lastname: userData.middleName,
            login: userData.login,
            password: userData.password || "",
            email: userData.email,
            positions: userData.position,
            rights: userData.rights,
            status: selectedUser.status,
        };

        try {
            await userService.update(payload);
            showSuccess("Пользователь успешно обновлен");
            usersRefetch();
        } catch (e) {
            showError("Произошла ошибка при обновлении пользователя");
        }

        handleCloseModal();
    };

    return (
        <div className={styles.formWrapper}>
            <div className={styles.formCard}>
                <div className={pageStyles.commonSearchWrapper}>
                    <div className={pageStyles.searchInput}>
                        <span className={pageStyles.searchIcon}>🔍</span>
                        <input
                            className={pageStyles.searchField}
                            placeholder="Поиск пользователя по ФИО, логину или email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                <Box className={pageStyles.positionsGrid}>
                    {positions.map((position) => {
                        const users = usersByPosition[position] || [];

                        return (
                            <PositionUsersAccordion
                                key={position}
                                position={position}
                                users={users}
                                selectedUserId={selectedUsers[position] || ""}
                                searchQuery={searchQuery}
                                onChange={handleUserSelect(position)}
                            />
                        );
                    })}
                </Box>
            </div>

            {selectedUser && (
                <UpdateUserModal
                    user={selectedUser}
                    positionOptions={(positionsData ?? []).map((position) => ({
                        value: position.id,
                        label: position.title,
                    }))}
                    rightsOptions={(rightsData ?? []).map((right) => ({
                        value: right.id,
                        label: right.title,
                    }))}
                    onClose={handleCloseModal}
                    onSave={handleSaveUser}
                />
            )}

            <SnackbarComponent />
        </div>
    );
};

export default UpdateUser;